// Outermost frame: backdrop > shell > column, demo banner on every route, offline strip, skip link, error boundary.
import { Banner, Button, useToast } from './ui';
import { t, subscribeLocale, getLocale } from './i18n';
import { DemoBanner } from './demo/DemoBanner';
import { Component, useEffect, useState, useSyncExternalStore } from './app-owner/react';
import { navigate, useRoute } from './app-owner/router';

export function useLocale(): string {
  return useSyncExternalStore(subscribeLocale, getLocale, getLocale);
}

function useOnline(): boolean {
  const [on, setOn] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true); const down = () => setOn(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return on;
}

class Boundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { /* surfaced in the UI below */ }
  render() {
    if (!(this as any).state.failed) return (this as any).props.children;
    return (
      <main id="main" className="pl-screen ow-center" data-testid="error-state" role="alert">
        <h1 className="pl-h2">{t('owner.owner.err.title')}</h1>
        <p className="pl-muted">{t('owner.owner.err.sub')}</p>
        <Button size="lg" onClick={() => { navigate('/app/today'); location.reload(); }} data-testid="error-retry">{t('common.btn.retry')}</Button>
      </main>
    );
  }
}

/** Tags the shared toast markup with the QA contract ids (toast, toast-undo) until the UI kit carries them. */
function useToastTestIds(): void {
  useEffect(() => {
    const tag = () => {
      document.querySelectorAll('.pl-toast').forEach((el) => { if (!el.getAttribute('data-testid')) el.setAttribute('data-testid', 'toast'); });
      document.querySelectorAll('.pl-toast__action').forEach((el) => { if (!el.getAttribute('data-testid')) el.setAttribute('data-testid', 'toast-undo'); });
    };
    tag();
    const mo = new MutationObserver(tag);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
}

export function AppShell({ children }: { children?: any }) {
  useLocale();
  useToastTestIds();
  const online = useOnline();
  const toast = useToast();
  const path = useRoute().path;
  // Owner/demo toasts (e.g. "demo reset") never carry over onto customer-facing booking pages.
  // The "demo restarted" toast goes away on the first interaction after it.
  useEffect(() => {
    const off = () => {
      const msg = t('common.toast.demoReset');
      document.querySelectorAll('.pl-toast').forEach((el) => { if (el.textContent?.includes(msg) && !el.querySelector('.pl-toast__action')) toast.clear(); });
    };
    document.addEventListener('pointerdown', off, true);
    document.addEventListener('keydown', off, true);
    return () => { document.removeEventListener('pointerdown', off, true); document.removeEventListener('keydown', off, true); };
  }, []);
  useEffect(() => { if (/^\/(b|m|o)(\/|$)/.test(path)) toast.clear(); }, [path]);
  return (
    <div className="pl-backdrop" data-testid="app-root">
      <div className="pl-shell">
        <div className="pl-column">
          <a href="#main" className="pl-skip" onClick={(e: any) => { e.preventDefault(); (document.getElementById('main') as HTMLElement | null)?.focus(); }}>{t('common.a11y.skip')}</a>
          <DemoBanner />
          {!online ? <Banner tone="warning" icon="alert" sticky><span data-testid="offline-banner">{t('common.offline.banner')}</span></Banner> : null}
          <Boundary>{children}</Boundary>
        </div>
      </div>
    </div>
  );
}
